import { Color, getThemeForKey } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ClickEvent } from "../../events/ClickEvent";
import { BlurEvent } from "../..";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Image } from "../image/Image";
import { KeyEvent } from "../../events/KeyEvent";
import { RadioOptionPropDefinitions, RadioOptionProps } from "./RadioOptionProps";
import { RadioButtonStyles } from "./RadioButtonStyles";
import { RadioButtonGroup } from "./RadioButtonGroup";
import { RadioOption } from "./RadioOption";
import { RadioOptionStyles } from "./RadioOptionStyles";

export class RadioButton extends RadioOption {
    private image: Image;
    
    constructor(props?: Partial<RadioOptionProps>) {
        super(props);
        this._element.classList.add(RadioOptionStyles.optionBase);
        this.createImage();
        this.addImageElement();
    }

    override get group(): RadioButtonGroup {
        return super.group as RadioButtonGroup;
    }

    override set group(value: RadioButtonGroup) {
        super.group = value;
        this.buttonFillColor = this.group.buttonFillColor;
        this.buttonSize = this.group.buttonSize;
    }

    override syncInnerComponentIds() {
        super.syncInnerComponentIds();
        this.setInnerComponentId(this.image, "image");
    }

    override getFocusTarget(): HTMLElement {
        return this.image?._element;
    }

    public override getEventTarget(): HTMLElement {
        return this.image?._element;
    }

    override set selected(value: boolean) {
        super.selected = value;
        if (this.image != null)
            this.image.name = value ? ImageName.radioOn : ImageName.radioOff;
    }

    get buttonFillColor() {
        return this.group?.buttonFillColor;
    }

    set buttonFillColor(value: Color) {
        this.createImage();
        this.addImageElement();
        if (this.image != null)
            this.image.color = value ?? getThemeForKey("radioButton.image").color;
    }

    get buttonSize(): number {
        return this.group?.buttonSize;
    }

    set buttonSize(value: number) {
        if (this.image != null) {
            this.image.height = value;
            this.image.width = value;
        }
    }

    protected createDisplayValueLabel() {
        super.createDisplayValueLabel();
        this.setProps({ themeKey: "radioButton.displayValue" });
    }

    private createImage() {
        if (this.image != null)
            return;
        this.image = new Image({
            themeKey: "radioButton.image",
            id: this.createInnerComponentId("image")
        });
        if (this.buttonFillColor != null)
            this.image.color = this.buttonFillColor;
        this.image._element.tabIndex = 0;
        this.image._element.classList.add(RadioButtonStyles.image);
        this._syncEnabled();
        this.image.addClickListener((event: ClickEvent) => this.userSelected(event));
        this.image.addKeyDownListener((event: KeyEvent) => this.handleSelectionViaKey(event));
        this.image.addBlurListener((event: BlurEvent) => this.group.evaluateOptionBlur(event));
    }

    private addImageElement() {
        if (this._element.contains(this.image._element))
            return;
        if (this._element.contains(this.labelDisplayValue._element))
            this._element.insertBefore(this.image._element, this.labelDisplayValue._element);
        else
            this._element.appendChild(this.image._element);
    }

    override enable() {
        const imageElement = this.image?._element;
        if (imageElement != null) {
            imageElement.removeAttribute("disabled");
            imageElement.setAttribute("tabindex", this.focusable === true ? "0" : "-1");
            imageElement.classList.remove(RadioButtonStyles.imageDisabled);
            imageElement.classList.add(RadioButtonStyles.image);
            this.setProps({ ...getThemeForKey("radioButton.enabled") });
            this.buttonFillColor = this.group?.buttonFillColor;
        }
    }

    override disable() {
        const imageElement = this.image?._element;
        if (imageElement != null) {
            imageElement.setAttribute("disabled", "true");
            imageElement.setAttribute("tabindex", "-1");
            imageElement.classList.remove(RadioButtonStyles.image);
            imageElement.classList.add(RadioButtonStyles.imageDisabled);
            this.setProps({ ...getThemeForKey("radioButton.disabled") });
        }
    }

    override getPropertyDefinitions() {
        return RadioOptionPropDefinitions.getDefinitions();
    }

    override get serializationName() {
        return "radiobutton";
    }

    override get properName(): string {
        return "Radio Button";
    }

    override get shortName(): string {
        return "button";
    }

    override getBasicValue(): any {
        return this.value;
    }
}
ComponentTypes.registerComponentType("radiobutton", { constructor: RadioButton.prototype.constructor });
